import React, { useId, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Search, X, CornerDownLeft, SearchX, Map as MapIcon, Skull, MapPin, Swords, Gem, HelpCircle, BookOpen, Loader2, BoxIcon, ScrollText, FlaskConical, Trophy, UserRound, ArrowRight } from "lucide-react";
import { Highlight } from "./Highlight";
import { useGuideSearch } from "../../../hooks/useGuideSearch";
import { SearchGroup } from "../../../utils/guideSearch";
import { detectIntent } from "../../../utils/searchIntents";
import { useWorldLookup } from "../../../hooks/useWorldLookup";
import { EntityThumb } from "../../world/EntityThumb";
import { itemIdFromKey } from "../../../utils/worldFormat";
import { itemIdFromPrettyPath, monsterIdFromPrettyPath } from "../../../utils/prettyRoutes";
import type { EntityRef } from "../../../types/guide";

/** Respaldo para documentos sin entidad declarada: la ruta de su ficha contiene el ID exacto. */
const entityFromPath = (path: string): EntityRef | undefined => {
  const prettyMonster = monsterIdFromPrettyPath(path);
  if (prettyMonster !== undefined) return {
    type: 'monster',
    id: prettyMonster
  };
  const prettyItem = itemIdFromPrettyPath(path);
  if (prettyItem) return {
    type: 'item',
    id: prettyItem
  };
  const mid = /\/monstruo\/(\d+)/.exec(path)?.[1];
  if (mid) return {
    type: 'monster',
    id: Number(mid)
  };
  const key = /\/guia\/items\/([^/#?]+)/.exec(path)?.[1];
  return key ? {
    type: 'item',
    id: itemIdFromKey(decodeURIComponent(key))
  } : undefined;
};
type GuideSearchProps = {
  variant?: 'hero' | 'compact';
  suggestions?: string[];
};
const groupIcons: Record<SearchGroup, BoxIcon> = {
  Mapas: MapIcon,
  Monstruos: Skull,
  Spots: MapPin,
  Invasiones: Swords,
  'Ítems': Gem,
  'Preguntas frecuentes': HelpCircle,
  'Guías': BookOpen,
  Misiones: ScrollText,
  Recetas: FlaskConical,
  Eventos: Trophy,
  NPC: UserRound
};
export function GuideSearch({
  variant = 'compact',
  suggestions
}: GuideSearchProps) {
  const s = useGuideSearch();
  const listId = useId();
  const reduceMotion = useReducedMotion();
  const hero = variant === 'hero';
  const navigate = useNavigate();
  const {
    lookup
  } = useWorldLookup();
  const intent = useMemo(() => detectIntent(s.query, lookup), [s.query, lookup]);
  const showPanel = s.open && s.hasQuery;
  const openIntent = (to: string) => {
    navigate(to);
    s.setOpen(false);
    s.setQuery('');
    s.inputRef.current?.blur();
  };
  let index = -1;
  return <div className="relative" data-id="element-870" data-mp="lz52uve58j3x">
      <div className={`flex items-center gap-3 rounded-full border bg-[#0A1628] transition-colors duration-150 focus-within:border-[#D4AF37]/70 ${hero ? 'h-11 md:h-16 border-[#D4AF37]/40 px-4 md:px-6' : 'h-11 md:h-12 border-white/15 px-4'}`} data-id="element-871" data-mp="lw0nd1v8a0xy">
        <Search className={`shrink-0 text-[#D4AF37] ${hero ? 'h-4 w-4 md:h-6 md:w-6' : 'h-4 w-4'}`} aria-hidden="true" data-id="element-872" data-mp="785thi5hhqja" />
        <input ref={s.inputRef} type="search" role="combobox" aria-expanded={showPanel} aria-controls={listId} aria-autocomplete="list" aria-activedescendant={showPanel && s.results[s.active] ? `${listId}-${s.active}` : undefined} aria-label="Buscar en la guía" value={s.query} onChange={e => {
        s.setQuery(e.target.value);
        s.setOpen(true);
      }} onFocus={() => s.setOpen(true)} onBlur={() => s.setOpen(false)} onKeyDown={s.onKeyDown} placeholder="Busca un ítem, mapa, evento o escribe tu pregunta…" className={`min-w-0 flex-1 bg-transparent text-base text-white placeholder:text-sm placeholder:text-gray-500 focus:outline-none md:placeholder:text-base [&::-webkit-search-cancel-button]:hidden ${hero ? 'md:text-lg' : 'md:text-[15px]'}`} data-id="element-873" data-mp="36kiy3gf9oc9" />
        {s.query && <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => s.setQuery('')} aria-label="Borrar búsqueda" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-gray-400 transition-colors duration-150 hover:text-white" data-id="element-874" data-mp="6uak0ydx6i8y">
            <X className="h-4 w-4" data-id="element-875" data-mp="37rxhkypvdbmo" />
          </button>}
      </div>

      {hero && suggestions && suggestions.length > 0 && <div className="-mx-4 mt-2.5 flex items-center gap-1.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none] md:mx-0 md:mt-4 md:flex-wrap md:gap-2 md:overflow-visible md:px-0 md:pb-0 [&::-webkit-scrollbar]:hidden" data-id="element-876" data-mp="330xl1uknwvkp">
          <span className="shrink-0 text-xs text-gray-500 md:text-sm" data-id="element-877" data-mp="2t4cf970tjgft">Prueba con:</span>
          {suggestions.map(q => <button key={q} type="button" onClick={() => s.fill(q)} className="shrink-0 whitespace-nowrap rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs text-gray-300 transition-colors duration-150 hover:border-[#D4AF37]/40 hover:text-white md:px-3 md:py-1.5 md:text-sm" data-id="element-878" data-mp="3qpqi5ud0t9gw">
              {q}
            </button>)}
        </div>}

      <AnimatePresence data-id="element-879" data-mp="25dp2o8fyk2mx">
        {showPanel && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: reduceMotion ? 0 : 0.15,
        ease: [0.23, 1, 0.32, 1]
      }} className={`absolute inset-x-0 z-40 mt-2 overflow-hidden rounded-3xl border border-white/10 bg-[#0A1628] shadow-[0_20px_60px_rgba(0,0,0,0.6)] ${hero ? 'top-11 md:top-16' : 'top-11 md:top-12'}`} data-id="element-880" data-mp="25erymus50v9p">
            {s.worldStatus === 'loading' && <p className="flex items-center gap-2 border-b border-white/5 px-5 py-2.5 text-xs text-gray-400" data-id="element-881" data-mp="um99nvatxwby">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#D4AF37]" aria-hidden="true" data-id="element-882" data-mp="zkasqg899vnf" />
                Cargando mapas, monstruos e ítems…
              </p>}
            {intent && <div className="border-b border-white/5 p-3" data-id="element-883" data-mp="18mac3f6qtbtj">
                <p className="px-1 pb-2 text-xs font-semibold text-[#D4AF37]" data-id="element-884" data-mp="1dkbvy2bztf62">{intent.title}</p>
                <div className={`grid gap-2 ${intent.options.length === 2 ? 'sm:grid-cols-2' : intent.options.length > 2 ? 'grid-cols-2 sm:grid-cols-4' : ''}`} data-id="element-885" data-mp="3aclsawyruype">
                  {intent.options.map((o, i) => <button key={o.to} type="button" onMouseDown={e => e.preventDefault()} onClick={() => openIntent(o.to)} className={`group flex min-h-[44px] items-center justify-between gap-2 rounded-2xl border px-3 py-2 text-left transition-colors duration-150 ${i === 0 ? 'border-[#D4AF37]/50 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/15' : 'border-white/10 hover:border-[#D4AF37]/40'} ${intent.options.length > 2 && i === 0 ? 'col-span-2 sm:col-span-4' : ''}`} data-id="element-886" data-mp="3fdjgbq6gqmyt" data-mpi={i}>
                      <span className="min-w-0" data-id="element-887" data-mp="3q8bpjo4zm8bf">
                        <span className="block text-sm font-semibold text-white" data-id="element-888" data-mp="3m8jd30ch3i0s">{o.label}</span>
                        {o.description && <span className="block text-xs text-gray-400" data-id="element-889" data-mp="3utqw97z43t0">{o.description}</span>}
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-890" data-mp="3v1weyrsiltz0" />
                    </button>)}
                </div>
              </div>}
            {s.results.length > 0 ? <div id={listId} role="listbox" aria-label="Resultados de la guía" className="max-h-[60vh] overflow-y-auto p-2" data-id="element-891" data-mp="8ug26d48r9z6">
                {s.groups.map(g => {
            const Icon = groupIcons[g.group];
            return <div key={g.group} role="group" aria-label={g.group} className="mb-1 last:mb-0" data-id="element-892" data-mp="wfh196hc7va5">
                      <p className="px-3 pb-1 pt-2.5 text-xs font-semibold text-gray-500" data-id="element-893" data-mp="igh9vr2fx2aa">{g.group}</p>
                      {g.results.map(r => {
                index += 1;
                const i = index;
                const isActive = i === s.active;
                return <div key={r.doc.key} id={`${listId}-${i}`} role="option" aria-selected={isActive} onMouseDown={e => e.preventDefault()} onMouseEnter={() => s.setActive(i)} onClick={() => s.go(r)} className={`flex cursor-pointer gap-3 rounded-2xl px-3 py-3 transition-colors duration-100 ${isActive ? 'bg-white/[0.06]' : ''}`} data-id="element-894" data-mp="dhnyc46prlcr">
                            {/* Imagen por la entidad del resultado (tipo + ID exacto), con el mismo componente que las fichas.
                                Sin acción de clic (no abre el resultado) y sin foco: el teclado sigue en el buscador.
                                El icono de la sección solo aparece si esa entidad no tiene imagen. */}
                            <span className="mt-0.5 shrink-0" data-id="element-895" data-mp="18ril0tz28qc">
                              <EntityThumb entity={r.doc.entity ?? entityFromPath(r.doc.path)} name={r.doc.title} focusable={false} fallback={<span className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37]" data-id="element-897" data-mp="3sh59nh04x2me">
                                  <Icon className="h-4 w-4" aria-hidden="true" data-id="element-898" data-mp="3iog0iss240u8" />
                                </span>} data-id="element-896" data-mp="16wuxerbg2ik" />
                            </span>
                            <span className="min-w-0 flex-1" data-id="element-899" data-mp="39ojllivgygcd">
                              <span className="block text-[15px] font-semibold leading-snug text-white" data-id="element-900" data-mp="39qe4ysr3e0ec">
                                <Highlight text={r.doc.title} terms={r.terms} data-id="element-901" data-mp="34oov3gflvsru" />
                                {r.doc.sectionTitle && <span className="font-normal text-gray-400" data-id="element-902" data-mp="2zsghi7h247ms"> › {r.doc.sectionTitle}</span>}
                              </span>
                              <span className="mt-0.5 block text-xs text-[#D4AF37]/80" data-id="element-903" data-mp="2ul07pp0ffta3">
                                {r.doc.typeLabel}
                                {r.doc.context ? ` · ${r.doc.context}` : ''}
                              </span>
                              <span className="mt-1.5 block text-sm leading-relaxed text-gray-400" data-id="element-904" data-mp="34ixue82n1i0l">
                                <span className="text-gray-500" data-id="element-905" data-mp="26tuiwkcidqnn">Coincide en {r.reasonLabel.toLowerCase()}: </span>
                                <Highlight text={r.snippet} terms={r.terms} data-id="element-906" data-mp="2bsp947ct2hhs" />
                              </span>
                            </span>
                            {isActive && <CornerDownLeft className="mt-1 hidden h-4 w-4 shrink-0 text-gray-500 md:block" aria-hidden="true" data-id="element-907" data-mp="2llmviokx4qlo" />}
                          </div>;
              })}
                    </div>;
          })}
              </div> : intent ? <div id={listId} role="status" className="sr-only" data-id="element-908" data-mp="1t2fxy7z1jfdy">Sin otros resultados</div> : <div id={listId} role="status" className="flex items-start gap-3 p-5" data-id="element-909" data-mp="2kmkfgh11qrrn">
                <SearchX className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-910" data-mp="2pkvposv974ae" />
                <p className="text-[15px] leading-relaxed text-gray-300" data-id="element-911" data-mp="2uml48ihoq038">
                  No encontramos información publicada sobre esa búsqueda. Prueba otro término o consulta al staff.
                </p>
              </div>}
            {s.results.length > 0 && <p className="hidden border-t border-white/5 px-5 py-2.5 text-xs text-gray-500 md:block" data-id="element-912" data-mp="26vp5t2iq46n0">
                ↑ ↓ para moverte · Enter para abrir · Esc para cerrar
              </p>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}